/*
 * Peter Beales Roses - My Garden (saved roses + garden journal)
 * ---------------------------------------------------------------------------
 * - "Save to My Garden" buttons across the site (browser wishlist).
 * - A count badge on any nav link to the My Garden page.
 * - The My Garden page: a "planting" per group of a variety in one place, so
 *   the same rose can be tracked in several spots. Roses ordered from us are
 *   pulled in automatically; the customer can add others and remove any.
 *   Per planting: location, planted date, quantity, notes, care log. A
 *   "this month" panel with seasonal product tips.
 *
 * Local to the browser by default. When the customer is logged in AND the
 * cloud backend is configured (window.__pbGardenSync set by the theme, see
 * MY-GARDEN-CLOUD-PLAN.md), every change also syncs to Supabase through the
 * Shopify App Proxy at that path — so the same garden shows up on any
 * device. localStorage stays the instant, offline-first source of truth;
 * the cloud copy is a debounced mirror, merged newest-write-wins per
 * planting. `pb:garden:updated` still fires on every change for Klaviyo etc.
 */
(function () {
  'use strict';

  var KEY = 'pb_my_garden';
  var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  // Shared vocabulary for both the quick-log actions and the manual dropdown,
  // so a care-log entry reads the same regardless of which UI created it.
  var TREATMENTS = ['Prune', 'Deadhead', 'Feed', 'Mulch', 'Water', 'Treated', 'Note', 'Other'];
  var QUICK_LOG_TYPES = ['Prune', 'Feed', 'Water', 'Treated']; // + Note, handled separately (no plural mismatch)

  function uid() { return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
  function nowIso() { return new Date().toISOString(); }

  /* ---------- store ---------- */

  function read() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(KEY)); } catch (e) { raw = null; }
    if (Array.isArray(raw)) raw = { saved: raw.map(function (i) { return i.handle; }).filter(Boolean) };
    if (!raw || typeof raw !== 'object') raw = {};
    raw.saved = Array.isArray(raw.saved) ? raw.saved : [];
    raw.locations = Array.isArray(raw.locations) ? raw.locations : [];
    raw.dismissed = Array.isArray(raw.dismissed) ? raw.dismissed : [];
    if (raw.journal && !raw.plantings) {
      raw.plantings = Object.keys(raw.journal).map(function (h) {
        var j = raw.journal[h] || {};
        return { id: uid(), handle: h, title: '', custom: false, qty: 1,
          location: j.location || '', locationNote: j.locationNote || '',
          plantedOn: j.plantedOn || '', notes: j.notes || '', treatments: j.treatments || [] };
      });
      delete raw.journal;
    }
    raw.plantings = Array.isArray(raw.plantings) ? raw.plantings : [];
    raw.pendingDeletes = Array.isArray(raw.pendingDeletes) ? raw.pendingDeletes : [];
    raw.pendingTreatmentDeletes = Array.isArray(raw.pendingTreatmentDeletes) ? raw.pendingTreatmentDeletes : [];
    return raw;
  }
  function write(store) {
    try { localStorage.setItem(KEY, JSON.stringify(store)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('pb:garden:updated', { detail: store }));
    paintButtons();
    paintCount();
    scheduleSync();
  }
  function isSaved(handle) { return read().saved.indexOf(handle) > -1; }
  function toggleSaved(handle) {
    var s = read();
    var i = s.saved.indexOf(handle);
    if (i > -1) s.saved.splice(i, 1); else s.saved.unshift(handle);
    write(s);
  }

  /* ---------- save buttons + nav count ---------- */

  function paintButtons() {
    document.querySelectorAll('[data-garden-toggle]').forEach(function (el) {
      var saved = isSaved(el.getAttribute('data-handle'));
      el.classList.toggle('is-saved', saved);
      var btn = el.querySelector('.garden-button__btn');
      if (btn) btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var add = el.querySelector('[data-garden-label-add]');
      var done = el.querySelector('[data-garden-label-saved]');
      if (add && done) { add.hidden = saved; done.hidden = !saved; }
    });
  }
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-garden-toggle]');
    if (!el) return;
    e.preventDefault();
    toggleSaved(el.getAttribute('data-handle'));
  });

  function paintCount() {
    var n = read().saved.length;
    var page = window.__pbGardenPage;
    if (!page) return;
    document.querySelectorAll('a[href$="' + page + '"], a[href*="' + page + '?"]').forEach(function (a) {
      if (a.closest('[data-my-garden]')) return;
      var badge = a.querySelector('.garden-nav-count');
      if (!badge) { badge = document.createElement('span'); badge.className = 'garden-nav-count'; a.appendChild(badge); }
      badge.textContent = n ? ' (' + n + ')' : '';
    });
  }

  /* ---------- helpers ---------- */

  function money(cents) {
    try {
      var iso = window.Shopify && Shopify.currency && Shopify.currency.active;
      return (cents / 100).toLocaleString(undefined, iso ? { style: 'currency', currency: iso } : undefined);
    } catch (e) { return '£' + (cents / 100).toFixed(2); }
  }
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return iso;
    return d.getDate() + ' ' + MONTHS[d.getMonth()].slice(0, 3) + ' ' + d.getFullYear();
  }
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
  function node(html) { var t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }

  /* ---------- toast (small, non-blocking confirmation) ---------- */

  var toastEl, toastTimer;
  function toast(message, actionLabel, onAction) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pb-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    clearTimeout(toastTimer);
    while (toastEl.firstChild) toastEl.removeChild(toastEl.firstChild);

    var msg = document.createElement('span');
    msg.textContent = message;
    toastEl.appendChild(msg);

    if (actionLabel && typeof onAction === 'function') {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pb-toast__action';
      btn.textContent = actionLabel;
      btn.addEventListener('click', function () {
        clearTimeout(toastTimer);
        hideToast();
        onAction();
      });
      toastEl.appendChild(btn);
    }

    void toastEl.offsetWidth; // force reflow so the enter transition runs
    toastEl.classList.add('is-visible');
    toastTimer = setTimeout(hideToast, actionLabel ? 8000 : 3500);
  }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-visible'); }

  function lastTreatment(pl, types) {
    var items = (pl.treatments || []).filter(function (t) { return types.indexOf(t.type) > -1; })
      .sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
    return items[0];
  }
  function dueState(pl) {
    var month = new Date().getMonth() + 1, year = new Date().getFullYear();
    var prune = lastTreatment(pl, ['Prune']);
    var feed = lastTreatment(pl, ['Feed']);
    if ((month === 2 || month === 3) && (!prune || new Date(prune.date).getFullYear() < year)) return 'prune';
    if (month >= 4 && month <= 8) {
      if (!feed) return 'feed';
      if ((Date.now() - new Date(feed.date).getTime()) / 6048e5 > 8) return 'feed';
    }
    return '';
  }

  /* ---------- cloud sync (cross-device, when logged in) ---------- */
  /* See MY-GARDEN-CLOUD-PLAN.md. The theme sets window.__pbGardenSync to the
   * App Proxy path (e.g. "/apps/pb-garden") only when cloud sync is turned on
   * in Theme settings, and window.__pbGardenCustomer to {id, email} when a
   * customer is logged in. Both must be present for any of this to run -
   * guests and stores without the backend configured are untouched and never
   * make a network call here. */

  var SYNC_PATH = (window.__pbGardenSync || '').replace(/\/+$/, '');
  var GARDEN_CUSTOMER = window.__pbGardenCustomer || null;
  var syncEnabled = !!(SYNC_PATH && GARDEN_CUSTOMER && GARDEN_CUSTOMER.id);
  var syncBusy = false, syncQueued = false, pushTimer;

  function syncUrl(path) { return SYNC_PATH + path; }

  function toServerPlanting(pl) {
    return {
      clientId: pl.id,
      handle: pl.handle || '',
      title: pl.title || '',
      custom: !!pl.custom,
      bareRoot: !!pl.bareRoot,
      qty: pl.qty || 1,
      location: pl.location || '',
      locationNote: pl.locationNote || '',
      plantedOn: pl.plantedOn || '',
      orderedOn: pl.orderedOn || '',
      notes: pl.notes || '',
      updatedAt: pl.updatedAt || nowIso(),
      treatments: (pl.treatments || []).filter(function (t) { return t && t.id; }).map(function (t) {
        return { clientId: t.id, type: t.type, date: t.date, note: t.note || '' };
      })
    };
  }

  function serverToLocal(sp, existing) {
    return {
      id: sp.clientId,
      handle: sp.handle || '',
      title: sp.title || 'Rose',
      custom: !!sp.custom,
      bareRoot: !!sp.bareRoot,
      qty: sp.qty || 1,
      location: sp.location || '',
      locationNote: sp.locationNote || '',
      plantedOn: sp.plantedOn || '',
      orderedOn: sp.orderedOn || '',
      notes: sp.notes || '',
      updatedAt: sp.updatedAt,
      treatments: (sp.treatments || []).map(function (t) {
        return { id: t.clientId, type: t.type, date: t.date, note: t.note || '' };
      }),
      images: sp.images || [],
      // These three only ever come from the storefront's own order history
      // (mergeOrders()), never from the server - carry them over from any
      // existing local copy so a synced planting keeps its product link/image.
      image: existing ? existing.image : '',
      url: existing ? existing.url : '',
      price: existing ? existing.price : 0,
      roseFamily: existing ? existing.roseFamily : '',
      repeatFlowering: existing ? existing.repeatFlowering : '',
      roseColour: existing ? existing.roseColour : '',
      fragrance: existing ? existing.fragrance : '',
      heightRange: existing ? existing.heightRange : ''
    };
  }

  // Folds a server snapshot into the local store: server wins per-planting
  // when its updatedAt is newer (or the planting is new to this device);
  // otherwise the local copy is left alone (it'll win the next push).
  // Always safe to call with an empty/failed response - callers check first.
  function applyServerGarden(serverPlantings) {
    var s = read();
    var byClientId = {};
    serverPlantings.forEach(function (sp) { byClientId[sp.clientId] = true; });

    serverPlantings.forEach(function (sp) {
      var local = s.plantings.filter(function (p) { return p.id === sp.clientId; })[0];
      var serverNewer = !local || !local.updatedAt || !sp.updatedAt || new Date(sp.updatedAt) > new Date(local.updatedAt);
      if (!local) {
        s.plantings.push(serverToLocal(sp, null));
      } else if (serverNewer) {
        var merged = serverToLocal(sp, local);
        Object.keys(merged).forEach(function (k) { local[k] = merged[k]; });
      } else {
        local.images = sp.images || local.images || [];
      }
    });

    // A planting the server no longer has: drop it locally only if we're the
    // one who deleted it (it's in pendingDeletes). Anything else not yet on
    // the server is either brand new (about to be pushed) or was deleted on
    // another device - either way we leave it for the next push/pull to
    // reconcile rather than guessing.
    s.plantings = s.plantings.filter(function (p) {
      if (byClientId[p.id]) return true;
      return s.pendingDeletes.indexOf(p.id) === -1;
    });

    s.pendingDeletes = [];
    s.pendingTreatmentDeletes = [];
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {}
    return s;
  }

  function pullGarden() {
    if (!syncEnabled) return Promise.resolve();
    return fetch(syncUrl('/garden'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.plantings) return;
        applyServerGarden(data.plantings);
        paintButtons();
        paintCount();
        if (page) draw();
      })
      .catch(function () {});
  }

  function scheduleSync() {
    if (!syncEnabled) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(pushGarden, 2000);
  }

  function pushGarden() {
    if (!syncEnabled) return;
    if (syncBusy) { syncQueued = true; return; }
    var s = read();
    syncBusy = true;
    fetch(syncUrl('/garden/sync'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: GARDEN_CUSTOMER.email || '',
        plantings: s.plantings.map(toServerPlanting),
        deletedClientIds: s.pendingDeletes,
        deletedTreatmentClientIds: s.pendingTreatmentDeletes
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        syncBusy = false;
        if (data && data.plantings) {
          applyServerGarden(data.plantings);
          paintButtons();
          paintCount();
          if (page) draw();
        }
        if (syncQueued) { syncQueued = false; scheduleSync(); }
      })
      .catch(function () { syncBusy = false; });
  }

  /* ---------- photo upload (cloud sync only) ---------- */

  function resizeImage(file, maxEdge) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        var w = img.width, h = img.height;
        var scale = Math.min(1, maxEdge / Math.max(w, h));
        var cw = Math.round(w * scale), ch = Math.round(h * scale);
        var canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
        canvas.toBlob(function (blob) {
          if (blob) resolve(blob); else reject(new Error('canvas export failed'));
        }, 'image/jpeg', 0.85);
      };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('could not read image')); };
      img.src = url;
    });
  }

  function uploadPhoto(pl, file, onDone) {
    if (!syncEnabled || !file) return;
    resizeImage(file, 1600).then(function (blob) {
      return fetch(syncUrl('/garden/upload-url'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plantingClientId: pl.id, filename: file.name || 'photo.jpg', contentType: 'image/jpeg' })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('could not get an upload link')); })
        .then(function (grant) {
          return fetch(grant.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob });
        });
    }).then(function () {
      pullGarden();
      if (typeof onDone === 'function') onDone(null);
    }).catch(function (err) {
      if (typeof onDone === 'function') onDone(err);
    });
  }

  /* ---------- My Garden page ---------- */

  var page;
  var focusNotesOnOpen = false; // set by "Add a garden note", read once when the detail view opens

  function renderPage(root) {
    page = {
      root: root,
      list: root.querySelector('[data-garden-plantings]'),
      savedGroup: root.querySelector('[data-garden-saved-group]'),
      savedList: root.querySelector('[data-garden-saved]'),
      empty: root.querySelector('[data-garden-empty]'),
      loading: root.querySelector('[data-garden-loading]'),
      summary: root.querySelector('[data-garden-summary]'),
      stats: root.querySelector('[data-garden-stats]'),
      heroActions: root.querySelector('[data-garden-hero-actions]'),
      overview: root.querySelector('[data-garden-overview]'),
      detail: root.querySelector('[data-garden-detail]'),
      fab: root.querySelector('[data-garden-fab]'),
      logModal: root.querySelector('[data-garden-log-modal]'),
      photoModal: root.querySelector('[data-garden-photo-modal]'),
      careGuideUrl: root.getAttribute('data-care-guide-url') || '',
      careGuideLabel: root.getAttribute('data-care-guide-label') || 'Rose care guide',
      calendar: root.querySelector('[data-garden-calendar]'),
      calHeading: root.querySelector('[data-garden-calendar-heading]'),
      calList: root.querySelector('[data-garden-calendar-list]'),
      tipsOut: root.querySelector('[data-garden-tips-out]'),
      toolbar: root.querySelector('[data-garden-toolbar]'),
      dismissedWrap: root.querySelector('[data-garden-dismissed]'),
      bareNote: root.getAttribute('data-bare-root-note') || 'Bare-root',
      pottedNote: root.getAttribute('data-potted-note') || 'Potted',
      defaultLocations: (root.getAttribute('data-location-options') || 'Border,Bed,Pot,Hedge,Other').split(',').map(function (s) { return s.trim(); }).filter(Boolean)
    };
    page.calHeadingTemplate = page.calHeading ? page.calHeading.textContent : '';
    try { page.orders = JSON.parse(root.querySelector('[data-garden-orders]').textContent) || []; } catch (e) { page.orders = []; }
    try { page.care = JSON.parse(root.querySelector('[data-garden-care]').textContent) || {}; } catch (e) { page.care = {}; }
    try { page.tips = JSON.parse(root.querySelector('[data-garden-tips]').textContent) || {}; } catch (e) { page.tips = {}; }

    // Sync-enabled devices must hear from the server BEFORE reconciling order
    // history against local storage - otherwise a fresh browser (empty
    // localStorage) has no way to recognise that a rose from this customer's
    // orders is already a synced planting, and mergeOrders() below would
    // generate it a brand-new id, producing a duplicate on the next push.
    // draw() once immediately so the page isn't blank while that pull is in
    // flight; mergeOrders() only runs once local storage reflects the server
    // (or immediately, if this device has no cloud sync to wait for).
    draw();
    routeFromHash();
    if (syncEnabled) {
      pullGarden().then(function () {
        mergeOrders();
        // Self-heals browsers that already picked up duplicate order-roses
        // before this fix shipped - a corrected code path can't undo damage
        // an old copy of this file already wrote to this device's storage.
        if (dedupeByHandle()) pushGarden();
        draw();
        routeFromHash(); // re-check: a direct #/rose/<id> link may only now have its data
      });
    } else {
      mergeOrders();
      draw();
      routeFromHash();
    }
  }

  /* ---------- My Rose Journal router: #/rose/<clientId> <-> the list ---------- */

  function parseRoseHash() {
    var m = /^#\/rose\/(.+)$/.exec(location.hash);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function routeFromHash() {
    if (!page) return;
    var id = parseRoseHash();
    if (id) {
      var pl = read().plantings.filter(function (p) { return p.id === id; })[0];
      if (pl) { showDetail(pl); return; }
    }
    showList();
  }
  window.addEventListener('hashchange', routeFromHash);

  function showList() {
    if (!page.detail || page.detail.hidden) return; // already showing the list - avoid re-drawing on every unrelated hashchange
    page.detail.hidden = true;
    page.detail.innerHTML = '';
    if (page.overview) page.overview.hidden = false;
    if (page.fab) page.fab.hidden = true;
    draw(); // the list may be stale after edits made inside the journal
  }

  function showDetail(pl) {
    if (page.overview) page.overview.hidden = true;
    if (page.fab) { page.fab.hidden = false; page.fab.onclick = function () { openLogModal(pl, lastActivityType()); }; }
    renderDetail(pl);
    page.detail.hidden = false;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  /* ---------- My Rose Journal: the individual rose view ---------- */

  var ACTIVITY_LABELS = { Prune: 'Pruned', Feed: 'Fed', Water: 'Watered', Treated: 'Treated', Note: 'Note' };

  // Remembered across sessions (which quick-log button opens from the FAB) -
  // additive to the store shape, doesn't touch anything that syncs.
  function lastActivityType() { return read().lastActivityType || 'Prune'; }
  function setLastActivityType(type) {
    var s = read();
    s.lastActivityType = type;
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {}
  }

  function aboutFacts(pl) {
    var facts = [];
    if (pl.roseFamily) facts.push(['Family', pl.roseFamily]);
    if (pl.roseColour) facts.push(['Colour', pl.roseColour]);
    if (pl.fragrance) facts.push(['Fragrance', pl.fragrance]);
    if (pl.repeatFlowering && /yes|true/i.test(pl.repeatFlowering)) facts.push(['Flowering', 'Repeat flowering']);
    if (pl.heightRange) facts.push(['Height', pl.heightRange]);
    return facts;
  }

  function gardenLine(pl) {
    var bits = [];
    if (pl.location) bits.push(pl.location + (pl.locationNote ? ' (' + pl.locationNote + ')' : ''));
    if (pl.plantedOn) bits.push('Planted ' + fmtDate(pl.plantedOn));
    if (pl.qty && pl.qty > 1) bits.push(pl.qty + ' plants');
    return bits.join(' · ');
  }

  // Re-renders whichever rose journal is open (if any), keeping it on the
  // given tab - used after a quick-log save so the new entry appears without
  // a full page reload.
  function refreshDetail(id, tab) {
    if (!page.detail || page.detail.hidden) return;
    var pl = read().plantings.filter(function (p) { return p.id === id; })[0];
    if (pl) renderDetail(pl, tab);
  }

  function showTab(root, name) {
    root.querySelectorAll('[role="tab"]').forEach(function (tab) {
      var selected = tab.getAttribute('data-tab') === name;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });
    root.querySelectorAll('[role="tabpanel"]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-panel') !== name;
    });
  }

  // WAI-ARIA "manual activation" tabs: arrow keys move focus between tabs,
  // Enter/Space activates the focused one. Closes the gap in the theme's own
  // <collection-tabs>, which only handles click.
  function wireTabs(root) {
    var tablist = root.querySelector('[role="tablist"]');
    if (!tablist) return;

    tablist.querySelectorAll('[role="tab"]').forEach(function (tab) {
      tab.addEventListener('click', function () { showTab(root, tab.getAttribute('data-tab')); });
    });

    tablist.addEventListener('keydown', function (e) {
      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showTab(root, tabs[current].getAttribute('data-tab')); return; }
      else return;
      e.preventDefault();
      tabs.forEach(function (t, i) { t.tabIndex = i === next ? 0 : -1; });
      tabs[next].focus();
    });
  }

  function paintOverviewPanel(panel, pl) {
    var facts = aboutFacts(pl);
    panel.innerHTML =
      (facts.length
        ? '<div class="my-garden__about">' +
            '<h2 class="my-garden__panel-heading">About this rose</h2>' +
            '<dl class="my-garden__fact-list">' +
              facts.map(function (f) { return '<div class="my-garden__fact"><dt>' + esc(f[0]) + '</dt><dd>' + esc(f[1]) + '</dd></div>'; }).join('') +
            '</dl>' +
          '</div>' : '') +
      '<div class="my-garden__notes">' +
        '<h2 class="my-garden__panel-heading">Your notes</h2>' +
        '<textarea data-garden-notes rows="4" placeholder="How it’s doing, moves, cuttings taken…">' + esc(pl.notes) + '</textarea>' +
      '</div>';

    panel.querySelector('[data-garden-notes]').addEventListener('input', debounce(function (e) {
      updatePlanting(pl.id, { notes: e.target.value });
      pl.notes = e.target.value;
    }, 400));
  }

  function paintCareLogPanel(panel, pl) {
    var cur = read().plantings.filter(function (p) { return p.id === pl.id; })[0] || pl;
    var items = (cur.treatments || []).slice().sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });

    if (!items.length) {
      panel.innerHTML = '<p class="my-garden__empty-note">Nothing logged yet — use the buttons above to start this rose’s care log.</p>';
      return;
    }

    var groups = [];
    items.forEach(function (t) {
      var group = groups[groups.length - 1];
      if (!group || group.date !== t.date) { group = { date: t.date, items: [] }; groups.push(group); }
      group.items.push(t);
    });

    panel.innerHTML = '<ul class="my-garden__care-log">' +
      groups.map(function (g) {
        return '<li class="my-garden__care-log-day">' +
          '<span class="my-garden__care-log-date">' + esc(fmtDate(g.date)) + '</span>' +
          '<ul class="my-garden__care-log-entries">' +
            g.items.map(function (t) {
              return '<li class="my-garden__care-log-entry" data-t-id="' + esc(t.id) + '">' +
                '<span class="my-garden__care-log-type">' + esc(ACTIVITY_LABELS[t.type] || t.type) + '</span>' +
                (t.note ? '<p class="my-garden__care-log-note">' + esc(t.note) + '</p>' : '') +
                '<button type="button" class="my-garden__link-btn my-garden__link-btn--danger" data-t-del aria-label="Delete this entry">Delete</button>' +
              '</li>';
            }).join('') +
          '</ul>' +
        '</li>';
      }).join('') +
    '</ul>';

    panel.querySelectorAll('[data-t-del]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tid = btn.closest('[data-t-id]').getAttribute('data-t-id');
        var s = read();
        var cur2 = s.plantings.filter(function (p) { return p.id === pl.id; })[0];
        if (!cur2) return;
        var had = (cur2.treatments || []).some(function (t) { return t.id === tid; });
        cur2.treatments = (cur2.treatments || []).filter(function (t) { return t.id !== tid; });
        if (had && s.pendingTreatmentDeletes.indexOf(tid) === -1) s.pendingTreatmentDeletes.push(tid);
        cur2.updatedAt = nowIso();
        write(s);
        if (syncEnabled) { clearTimeout(pushTimer); pushGarden(); }
        paintCareLogPanel(panel, pl);
      });
    });
  }

  function paintPhotosPanel(panel, pl) {
    var cur = read().plantings.filter(function (p) { return p.id === pl.id; })[0] || pl;
    var images = cur.images || [];

    panel.innerHTML =
      (images.length
        ? '<ul class="my-garden__photo-grid">' +
            images.map(function (img, i) {
              var alt = img.caption ? img.caption : (pl.title || 'Rose') + ' — photo added by you';
              return '<li><button type="button" class="my-garden__photo-thumb" data-photo-open="' + i + '"><img src="' + esc(img.thumbUrl || img.url) + '" alt="' + esc(alt) + '" loading="lazy"></button></li>';
            }).join('') +
          '</ul>'
        : '<p class="my-garden__empty-note">No photos yet — add one to remember how this rose looks.</p>') +
      '<label class="my-garden__photo-add button button--outline button--small">Add a photo' +
        '<input type="file" accept="image/*" data-garden-photo-add hidden>' +
      '</label>' +
      '<span class="my-garden__photo-status" data-garden-photo-add-status hidden></span>';

    panel.querySelectorAll('[data-photo-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var img = images[parseInt(btn.getAttribute('data-photo-open'), 10)];
        if (!img || !page.photoModal) return;
        var alt = img.caption ? img.caption : (pl.title || 'Rose') + ' — photo added by you';
        var imgEl = page.photoModal.querySelector('[data-garden-photo-img]');
        imgEl.src = img.url || img.thumbUrl;
        imgEl.alt = alt;
        var caption = page.photoModal.querySelector('[data-garden-photo-caption]');
        caption.textContent = img.caption || '';
        caption.hidden = !img.caption;
        page.photoModal.show();
      });
    });

    var addInput = panel.querySelector('[data-garden-photo-add]');
    var addStatus = panel.querySelector('[data-garden-photo-add-status]');
    addInput.addEventListener('change', function () {
      var file = addInput.files && addInput.files[0];
      addInput.value = '';
      if (!file) return;
      addStatus.hidden = false;
      addStatus.textContent = 'Uploading…';
      uploadPhoto(pl, file, function (err) {
        addStatus.textContent = err ? 'Could not upload that photo — try again.' : 'Photo added.';
        setTimeout(function () { addStatus.hidden = true; }, 3000);
        if (!err) paintPhotosPanel(panel, pl);
      });
    });
  }

  // "Edit details" - deliberately secondary to the quick-log actions above
  // it. Built lazily on first open, reusing the same location/qty/planted
  // fields buildJournal() used to render inline.
  function buildEditPanel(panel, pl, root) {
    panel.dataset.built = '1';
    var locOpts = ['<option value="">Where is it planted?</option>']
      .concat(locations().map(function (l) { return '<option value="' + esc(l) + '"' + (pl.location === l ? ' selected' : '') + '>' + esc(l) + '</option>'; }))
      .concat(['<option value="__new">+ Add a new location…</option>']).join('');

    panel.innerHTML =
      '<div class="my-garden__field-row">' +
        '<label>Location <select data-e-location>' + locOpts + '</select></label>' +
        '<label>How many <input type="number" min="1" data-e-qty value="' + (pl.qty || 1) + '"></label>' +
      '</div>' +
      '<div class="my-garden__field"><input type="text" data-e-locnote placeholder="e.g. north wall by the shed" value="' + esc(pl.locationNote) + '"></div>' +
      '<div class="my-garden__field"><label>Planted <input type="date" data-e-planted value="' + esc(pl.plantedOn) + '"></label></div>' +
      '<button type="button" class="my-garden__link-btn" data-e-plant-another>Plant another somewhere else</button>';

    function refreshLine() {
      var line = root.querySelector('[data-garden-summary-line]');
      if (!line) return;
      var text = gardenLine(pl);
      line.textContent = text;
      line.hidden = !text;
    }

    var locSel = panel.querySelector('[data-e-location]');
    locSel.addEventListener('change', function () {
      if (locSel.value === '__new') { openLocationInput(locSel, pl, refreshLine); }
      else { updatePlanting(pl.id, { location: locSel.value }); pl.location = locSel.value; refreshLine(); }
    });
    panel.querySelector('[data-e-qty]').addEventListener('change', function (e) {
      var v = parseInt(e.target.value, 10) || 1;
      updatePlanting(pl.id, { qty: v }); pl.qty = v; refreshLine(); drawSummary(read());
    });
    panel.querySelector('[data-e-locnote]').addEventListener('input', debounce(function (e) {
      updatePlanting(pl.id, { locationNote: e.target.value }); pl.locationNote = e.target.value; refreshLine();
    }, 400));
    panel.querySelector('[data-e-planted]').addEventListener('change', function (e) {
      updatePlanting(pl.id, { plantedOn: e.target.value }); pl.plantedOn = e.target.value; refreshLine();
    });
    panel.querySelector('[data-e-plant-another]').addEventListener('click', function () {
      var s = read();
      var fresh = {
        id: uid(), handle: pl.handle, title: pl.title, custom: pl.custom, bareRoot: pl.bareRoot,
        image: pl.image, url: pl.url, price: pl.price, qty: 1, orderedOn: pl.orderedOn,
        roseFamily: pl.roseFamily || '', repeatFlowering: pl.repeatFlowering || '',
        roseColour: pl.roseColour || '', fragrance: pl.fragrance || '', heightRange: pl.heightRange || '',
        location: '', locationNote: '', plantedOn: '', notes: '', treatments: [], updatedAt: nowIso()
      };
      s.plantings.push(fresh);
      write(s);
      toast('Added another planting of ' + (pl.title || 'this rose') + ' to My Garden');
      location.hash = '#/rose/' + encodeURIComponent(fresh.id);
    });
  }

  function wireEditToggle(root, pl) {
    var toggle = root.querySelector('[data-garden-edit-toggle]');
    var editPanel = root.querySelector('[data-garden-edit]');
    if (!toggle || !editPanel) return;
    toggle.addEventListener('click', function () {
      var opening = editPanel.hidden;
      if (opening && !editPanel.dataset.built) buildEditPanel(editPanel, pl, root);
      editPanel.hidden = !opening;
      toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      toggle.textContent = opening ? 'Hide details' : 'Edit details';
    });
  }

  // Opens the shared quick-log modal for one activity type (Pruned/Fed/
  // Watered/Treated/Note). Reuses the existing treatment array and sync/
  // photo-upload mechanisms - this is not a new modal system, just a form
  // wrapped around the same <modal-box> the rest of the theme uses.
  function openLogModal(pl, type) {
    var modal = page.logModal;
    if (!modal) return;
    var form = modal.querySelector('[data-garden-log-form]');
    var titleEl = modal.querySelector('[data-garden-log-title]');
    var roseEl = modal.querySelector('[data-garden-log-rose]');
    var dateInput = modal.querySelector('[data-garden-log-date]');
    var noteInput = modal.querySelector('[data-garden-log-note]');
    var photoInput = modal.querySelector('[data-garden-log-photo]');
    var photoStatus = modal.querySelector('[data-garden-log-photo-status]');

    titleEl.textContent = type === 'Note' ? 'Add a note' : 'Log: ' + ACTIVITY_LABELS[type];
    roseEl.textContent = pl.title || 'Rose';
    dateInput.value = new Date().toISOString().slice(0, 10);
    noteInput.value = '';
    photoInput.value = '';
    photoStatus.hidden = true;
    photoStatus.textContent = '';

    form.onsubmit = function (e) {
      e.preventDefault();
      var s = read();
      var cur = s.plantings.filter(function (p) { return p.id === pl.id; })[0];
      if (!cur) { modal.hide(); return; }
      cur.treatments = cur.treatments || [];
      cur.treatments.push({
        id: uid(),
        type: type,
        date: dateInput.value || new Date().toISOString().slice(0, 10),
        note: noteInput.value.trim()
      });
      cur.updatedAt = nowIso();
      write(s);
      if (syncEnabled) { clearTimeout(pushTimer); pushGarden(); }
      setLastActivityType(type);

      var file = photoInput.files && photoInput.files[0];
      if (file && syncEnabled) {
        photoStatus.hidden = false;
        photoStatus.textContent = 'Uploading photo…';
        uploadPhoto(cur, file, function (err) {
          modal.hide();
          toast(err ? 'Saved — the photo could not be uploaded' : 'Saved to your garden journal');
          refreshDetail(pl.id, 'care');
        });
      } else {
        modal.hide();
        toast('Saved to your garden journal');
        refreshDetail(pl.id, 'care');
      }
    };

    modal.show();
  }

  function renderDetail(pl, activeTab) {
    if (!activeTab) {
      var curTab = page.detail.querySelector('[role="tab"][aria-selected="true"]');
      activeTab = curTab ? curTab.getAttribute('data-tab') : 'overview';
    }
    var variety = varietyLine(pl);
    var line = gardenLine(pl);
    var titleAttr = esc(pl.title || 'Rose');

    page.detail.innerHTML =
      '<div class="my-garden__journal">' +
        '<a href="#" class="my-garden__back" data-garden-back>&larr; My Garden</a>' +
        '<div class="my-garden__journal-hero">' +
          (pl.image
            ? '<div class="my-garden__journal-image"><img src="' + esc(pl.image) + '" alt=""></div>'
            : '<div class="my-garden__journal-image my-garden__journal-image--none">' + NO_IMAGE_SVG + '</div>') +
          '<div class="my-garden__journal-heading">' +
            '<h1 class="my-garden__journal-title">' + titleAttr + '</h1>' +
            (variety ? '<p class="my-garden__journal-variety">' + esc(variety) + '</p>' : '') +
            '<p class="my-garden__journal-garden" data-garden-summary-line' + (line ? '' : ' hidden') + '>' + esc(line) + '</p>' +
            (!pl.custom ? '<p class="my-garden__journal-source">Purchased from Peter Beales</p>' : '') +
            '<button type="button" class="my-garden__link-btn" data-garden-edit-toggle aria-expanded="false">Edit details</button>' +
          '</div>' +
        '</div>' +
        '<div class="my-garden__journal-edit" data-garden-edit hidden></div>' +
        '<div class="my-garden__journal-log-actions">' +
          '<span class="my-garden__field-label">Add to your garden journal</span>' +
          '<div class="my-garden__quick-log-buttons">' +
            QUICK_LOG_TYPES.map(function (t) {
              return '<button type="button" class="button button--outline button--small" data-quick-log="' + t + '">' + esc(ACTIVITY_LABELS[t]) + '</button>';
            }).join('') +
            '<button type="button" class="button button--outline button--small" data-quick-log="Note">Note</button>' +
          '</div>' +
        '</div>' +
        '<div class="my-garden__tabs">' +
          '<div role="tablist" class="my-garden__tablist" aria-label="' + titleAttr + ' sections">' +
            '<button type="button" role="tab" id="garden-tab-overview" aria-controls="garden-panel-overview" data-tab="overview" aria-selected="' + (activeTab === 'overview' ? 'true' : 'false') + '" tabindex="' + (activeTab === 'overview' ? '0' : '-1') + '">Overview</button>' +
            '<button type="button" role="tab" id="garden-tab-care" aria-controls="garden-panel-care" data-tab="care" aria-selected="' + (activeTab === 'care' ? 'true' : 'false') + '" tabindex="' + (activeTab === 'care' ? '0' : '-1') + '">Care log</button>' +
            '<button type="button" role="tab" id="garden-tab-photos" aria-controls="garden-panel-photos" data-tab="photos" aria-selected="' + (activeTab === 'photos' ? 'true' : 'false') + '" tabindex="' + (activeTab === 'photos' ? '0' : '-1') + '">Photos</button>' +
          '</div>' +
          '<div role="tabpanel" id="garden-panel-overview" aria-labelledby="garden-tab-overview" data-panel="overview"' + (activeTab === 'overview' ? '' : ' hidden') + '></div>' +
          '<div role="tabpanel" id="garden-panel-care" aria-labelledby="garden-tab-care" data-panel="care"' + (activeTab === 'care' ? '' : ' hidden') + '></div>' +
          '<div role="tabpanel" id="garden-panel-photos" aria-labelledby="garden-tab-photos" data-panel="photos"' + (activeTab === 'photos' ? '' : ' hidden') + '></div>' +
        '</div>' +
        (page.careGuideUrl ? '<a class="my-garden__journal-care-link" href="' + esc(page.careGuideUrl) + '">' + esc(page.careGuideLabel) + ' &rarr;</a>' : '') +
      '</div>';

    var root = page.detail;
    paintOverviewPanel(root.querySelector('[data-panel="overview"]'), pl);
    paintCareLogPanel(root.querySelector('[data-panel="care"]'), pl);
    if (syncEnabled) paintPhotosPanel(root.querySelector('[data-panel="photos"]'), pl);
    else root.querySelector('[data-panel="photos"]').innerHTML = '<p class="my-garden__empty-note">Photos aren’t available right now.</p>';

    wireTabs(root);
    wireEditToggle(root, pl);

    root.querySelectorAll('[data-quick-log]').forEach(function (btn) {
      btn.addEventListener('click', function () { openLogModal(pl, btn.getAttribute('data-quick-log')); });
    });
    root.querySelector('[data-garden-back]').addEventListener('click', function (e) {
      e.preventDefault();
      location.hash = '';
    });

    if (focusNotesOnOpen) {
      focusNotesOnOpen = false;
      showTab(root, 'overview');
      var notesField = root.querySelector('[data-garden-notes]');
      if (notesField) notesField.focus();
    }
  }

  // Collapses local plantings that share a product handle down to one,
  // keeping whichever copy has the most real data (treatments/images/notes/
  // location) and queuing the rest for deletion - locally immediately, and
  // on the server via the next push. Custom roses (no handle) are untouched:
  // a customer can legitimately track the same custom entry more than once.
  function dedupeByHandle() {
    var s = read();
    var byHandle = {};
    s.plantings.forEach(function (p) {
      if (!p.handle) return;
      (byHandle[p.handle] = byHandle[p.handle] || []).push(p);
    });

    var changed = false;
    Object.keys(byHandle).forEach(function (handle) {
      var group = byHandle[handle];
      if (group.length < 2) return;
      group.sort(function (a, b) {
        var scoreDiff = richness(b) - richness(a);
        if (scoreDiff !== 0) return scoreDiff;
        return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
      });
      for (var i = 1; i < group.length; i++) {
        var idx = s.plantings.indexOf(group[i]);
        if (idx > -1) s.plantings.splice(idx, 1);
        if (s.pendingDeletes.indexOf(group[i].id) === -1) s.pendingDeletes.push(group[i].id);
        changed = true;
      }
    });

    if (changed) {
      try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {}
    }
    return changed;
  }

  function richness(p) {
    var score = 0;
    if (p.notes) score += 1;
    if (p.location) score += 1;
    if (p.plantedOn) score += 1;
    if (p.treatments && p.treatments.length) score += p.treatments.length * 2;
    if (p.images && p.images.length) score += p.images.length * 2;
    return score;
  }

  function locations() {
    var s = read();
    return page.defaultLocations.concat(s.locations.filter(function (l) { return page.defaultLocations.indexOf(l) === -1; }));
  }

  function mergeOrders() {
    var s = read();
    var changed = false;
    page.orders.forEach(function (o) {
      var exists = s.plantings.some(function (pl) { return pl.handle === o.handle; });
      if (exists || s.dismissed.indexOf(o.handle) > -1) return;
      s.plantings.push({
        id: uid(), handle: o.handle, title: o.title, custom: false, bareRoot: !!o.bareRoot,
        image: o.image, url: o.url, price: o.price, qty: o.qty || 1, orderedOn: o.orderedOn,
        roseFamily: o.roseFamily || '', repeatFlowering: o.repeatFlowering || '',
        roseColour: o.roseColour || '', fragrance: o.fragrance || '', heightRange: o.heightRange || '',
        location: '', locationNote: '', plantedOn: '', notes: '', treatments: [], updatedAt: nowIso()
      });
      changed = true;
    });
    if (changed) {
      try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {}
      scheduleSync();
    }
  }

  function updatePlanting(id, patch) {
    var s = read();
    var pl = s.plantings.filter(function (p) { return p.id === id; })[0];
    if (!pl) return;
    Object.keys(patch).forEach(function (k) { pl[k] = patch[k]; });
    pl.updatedAt = nowIso();
    write(s);
  }

  function savedOnlyHandles(s) {
    return s.saved.filter(function (h) {
      return !s.plantings.some(function (pl) { return pl.handle === h; });
    });
  }

  // Everything except the two lists: loading flag, empty/toolbar/calendar
  // visibility, calendar, summary, dismissed toggle. Cheap; safe to call often.
  function refreshAux(s) {
    if (page.loading) page.loading.hidden = true;
    var hasContent = s.plantings.length || savedOnlyHandles(s).length;
    if (page.empty) page.empty.hidden = !!hasContent;
    if (page.toolbar) page.toolbar.hidden = false;
    if (page.calendar) page.calendar.hidden = false;
    if (page.heroActions) page.heroActions.hidden = false;

    drawCalendar(s);
    drawStats(s);
    drawSummary(s);

    if (page.dismissedWrap) {
      if (s.dismissed.length) {
        page.dismissedWrap.hidden = false;
        page.dismissedWrap.querySelector('[data-garden-show-dismissed]').textContent =
          'Show ' + s.dismissed.length + ' removed rose' + (s.dismissed.length === 1 ? '' : 's');
      } else {
        page.dismissedWrap.hidden = true;
      }
    }
  }

  // Full rebuild of the planting list. Only used on first render and the
  // bulk actions; single add/remove touch the DOM directly so open journal
  // panels aren't lost.
  function renderPlantings(s) {
    page.list.innerHTML = '';
    s.plantings.forEach(function (pl) { page.list.appendChild(plantingCard(pl)); });
  }

  // Saved-but-not-planted roses. Skipped entirely unless the set changed,
  // so we don't re-fetch every product on every little edit.
  function renderSaved(s, force) {
    if (!page.savedGroup) return;
    var handles = savedOnlyHandles(s);
    var key = handles.slice().sort().join(',');
    if (!force && key === page.savedKey) return;
    page.savedKey = key;

    if (!handles.length) {
      page.savedGroup.hidden = true;
      page.savedList.innerHTML = '';
      return;
    }
    page.savedGroup.hidden = false;
    page.savedList.innerHTML = '';
    handles.forEach(function (handle) {
      var sk = node(
        '<li class="my-garden__item my-garden__item--saved my-garden__item--skeleton" aria-hidden="true">' +
          '<span class="my-garden__item-image my-garden__skel-box"></span>' +
          '<div class="my-garden__item-body">' +
            '<span class="my-garden__skel-line my-garden__skel-line--title"></span>' +
            '<span class="my-garden__skel-line my-garden__skel-line--price"></span>' +
          '</div>' +
        '</li>'
      );
      page.savedList.appendChild(sk);
      fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) { sk.replaceWith(savedCard(p)); } else { sk.remove(); } })
        .catch(function () { sk.remove(); });
    });
  }

  function draw() {
    var s = read();
    refreshAux(s);
    renderPlantings(s);
    renderSaved(s);
  }

  function drawCalendar(s) {
    var m = new Date().getMonth() + 1;
    if (page.calHeading && page.calHeadingTemplate) {
      page.calHeading.textContent = page.calHeadingTemplate.replace('[Month]', MONTHS[m - 1]).replace('[month]', MONTHS[m - 1]);
    }
    var jobs = (page.care[String(m)] || '').split('|').map(function (j) { return j.trim(); }).filter(Boolean);
    page.calList.innerHTML = jobs.map(function (j) { return '<li>' + esc(j) + '</li>'; }).join('') || '<li>Nothing pressing this month.</li>';

    // seasonal tips: pick tip types from this month's job text + any plant due states
    var text = (page.care[String(m)] || '').toLowerCase();
    var want = {};
    if (/prune|deadhead/.test(text)) want.prune = 1;
    if (/feed/.test(text)) want.feed = 1;
    if (/mulch/.test(text)) want.mulch = 1;
    s.plantings.forEach(function (pl) { var d = dueState(pl); if (d) want[d] = 1; });

    page.tipsOut.innerHTML = Object.keys(want).map(function (k) {
      var t = page.tips[k];
      if (!t || !t.tip) return '';
      var cta = t.url ? '<a class="my-garden__tip-link" href="' + esc(t.url) + '">' + esc(t.label || 'Shop') + ' &rarr;</a>' : '';
      return '<div class="my-garden__tip"><p>' + esc(t.tip) + '</p>' + cta + '</div>';
    }).join('');
  }

  // The one-line stat under the heading: "N roses · N planting locations".
  // Deliberately restrained - two figures, not a dashboard. A journal-entry
  // count can ride along in the same line without it reading as analytics.
  function drawStats(s) {
    if (!page.stats) return;
    var n = s.plantings.length;
    if (!n) { page.stats.hidden = true; return; }
    var locs = {};
    s.plantings.forEach(function (pl) { if (pl.location) locs[pl.location] = 1; });
    var locCount = Object.keys(locs).length;
    var entries = s.plantings.reduce(function (a, pl) { return a + (pl.treatments ? pl.treatments.length : 0); }, 0);

    var bits = [n + ' rose' + (n === 1 ? '' : 's')];
    if (locCount) bits.push(locCount + ' planting location' + (locCount === 1 ? '' : 's'));
    if (entries) bits.push(entries + ' journal ' + (entries === 1 ? 'entry' : 'entries'));

    page.stats.hidden = false;
    page.stats.textContent = bits.join(' · ');
  }

  function drawSummary(s) {
    if (!page.summary) return;
    var n = s.plantings.length;
    if (!n) { page.summary.hidden = true; return; }
    var total = s.plantings.reduce(function (a, pl) { return a + (parseInt(pl.qty, 10) || 1); }, 0);
    if (total <= n) { page.summary.hidden = true; return; }
    page.summary.hidden = false;
    page.summary.innerHTML = '<strong>' + total + '</strong> plants across ' + n + ' entries in your garden.';
  }

  // The rose's placeholder image: a restrained line-drawn rose, not a grey
  // box - used for custom roses with no product photo.
  var NO_IMAGE_SVG = '<svg class="my-garden__item-image-icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="24" cy="18" r="8"/><path d="M24 26c0 8-3 14-3 18M24 26c0 8 3 14 3 18M17 34c-4 2-6 5-7 9M31 34c4 2 6 5 7 9"/></svg>';

  function varietyLine(pl) {
    var bits = [];
    if (pl.roseFamily) bits.push(pl.roseFamily);
    if (pl.repeatFlowering && /yes|true/i.test(pl.repeatFlowering)) bits.push('Repeat flowering');
    return bits.join(' · ');
  }
  function lastActivityLine(pl) {
    var items = (pl.treatments || []).slice().sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
    if (!items.length) return '';
    return items[0].type + ' ' + fmtDate(items[0].date);
  }

  function plantingCard(pl) {
    var due = dueState(pl);
    var dueLabel = due === 'prune' ? 'Pruning due' : due === 'feed' ? 'Feed due' : '';
    var variety = varietyLine(pl);
    var activity = lastActivityLine(pl);
    var noteSnippet = (pl.notes || '').trim();
    var journalHref = '#/rose/' + encodeURIComponent(pl.id);

    var card = node(
      '<li class="my-garden__item my-garden__item--planting">' +
        (pl.image
          ? '<a class="my-garden__item-image" href="' + journalHref + '" aria-hidden="true" tabindex="-1"><img src="' + esc(pl.image) + '" alt="" loading="lazy"></a>'
          : '<a class="my-garden__item-image my-garden__item-image--none" href="' + journalHref + '" aria-hidden="true" tabindex="-1">' + NO_IMAGE_SVG + '</a>') +
        '<div class="my-garden__item-main">' +
          '<div class="my-garden__item-head">' +
            '<a class="my-garden__item-title" href="' + journalHref + '">' + esc(pl.title || 'Rose') + '</a>' +
            (dueLabel ? '<span class="my-garden__due">' + dueLabel + '</span>' : '') +
          '</div>' +
          (variety ? '<span class="my-garden__item-variety" data-variety>' + esc(variety) + '</span>' : '<span class="my-garden__item-variety" data-variety hidden></span>') +
          '<span class="my-garden__item-loc" data-loc-summary></span>' +
          '<span class="my-garden__item-activity" data-activity' + (activity ? '' : ' hidden') + '>' + (activity ? 'Last activity: ' + esc(activity) : '') + '</span>' +
          '<p class="my-garden__item-note" data-note' + (noteSnippet ? '' : ' hidden') + '>' + (noteSnippet ? '&ldquo;' + esc(noteSnippet) + '&rdquo;' : '') + '</p>' +
          '<a class="my-garden__toggle-journal" href="' + journalHref + '">View rose journal &rarr;</a>' +
          '<div class="my-garden__item-links">' +
            '<button type="button" class="my-garden__link-btn" data-garden-plant-another>Plant another somewhere else</button>' +
            '<button type="button" class="my-garden__link-btn my-garden__link-btn--danger" data-garden-remove-planting>Remove</button>' +
          '</div>' +
        '</div>' +
      '</li>'
    );

    var locSummary = card.querySelector('[data-loc-summary]');
    function paintLocSummary() {
      var bits = [];
      if (pl.location) bits.push(pl.location + (pl.locationNote ? ' (' + pl.locationNote + ')' : ''));
      if (pl.plantedOn) bits.push('Planted ' + fmtDate(pl.plantedOn));
      if (pl.qty && pl.qty > 1) bits.push(pl.qty + ' plants');
      locSummary.textContent = bits.join(' · ');
    }
    paintLocSummary();

    card.querySelector('[data-garden-plant-another]').addEventListener('click', function () {
      var s = read();
      var fresh = {
        id: uid(), handle: pl.handle, title: pl.title, custom: pl.custom, bareRoot: pl.bareRoot,
        image: pl.image, url: pl.url, price: pl.price, qty: 1, orderedOn: pl.orderedOn,
        roseFamily: pl.roseFamily || '', repeatFlowering: pl.repeatFlowering || '',
        roseColour: pl.roseColour || '', fragrance: pl.fragrance || '', heightRange: pl.heightRange || '',
        location: '', locationNote: '', plantedOn: '', notes: '', treatments: [], updatedAt: nowIso()
      };
      s.plantings.push(fresh);
      write(s);
      var newCard = plantingCard(fresh);
      page.list.appendChild(newCard);
      refreshAux(read());
      newCard.querySelector('.my-garden__toggle-journal').focus();
    });
    card.querySelector('[data-garden-remove-planting]').addEventListener('click', function () {
      var s = read();
      var idx = -1;
      for (var i = 0; i < s.plantings.length; i++) {
        if (s.plantings[i].id === pl.id) { idx = i; break; }
      }
      if (idx === -1) return;

      var removed = s.plantings[idx];
      s.plantings.splice(idx, 1);
      if (s.pendingDeletes.indexOf(pl.id) === -1) s.pendingDeletes.push(pl.id);
      // Deletes flush immediately rather than waiting on the debounced sync -
      // switching devices seconds after removing something shouldn't be able
      // to outrun the request that tells the server to remove it too.

      // if no plantings of this ordered handle remain, remember it as dismissed
      // so mergeOrders() doesn't pull it straight back in
      var markedDismissed = false;
      if (pl.handle && !pl.custom && !s.plantings.some(function (p) { return p.handle === pl.handle; })) {
        if (s.dismissed.indexOf(pl.handle) === -1) { s.dismissed.push(pl.handle); markedDismissed = true; }
      }
      write(s);
      if (syncEnabled) { clearTimeout(pushTimer); pushGarden(); }

      // remove just this card - other cards (and any open journal) stay put
      card.remove();
      refreshAux(read());
      renderSaved(read()); // the handle may now belong in the saved group

      toast('Rose removed from My Garden', 'Undo', function () {
        var s2 = read();
        s2.plantings.splice(Math.min(idx, s2.plantings.length), 0, removed);
        s2.pendingDeletes = s2.pendingDeletes.filter(function (id) { return id !== pl.id; });
        if (markedDismissed) {
          s2.dismissed = s2.dismissed.filter(function (h) { return h !== pl.handle; });
        }
        write(s2);
        if (syncEnabled) { clearTimeout(pushTimer); pushGarden(); }
        var restored = plantingCard(removed);
        var ref = page.list.children[idx] || null;
        page.list.insertBefore(restored, ref);
        refreshAux(read());
        renderSaved(read());
        toast('Rose restored to My Garden');
      });
    });

    return card;
  }

  // Inline "add a new location" - replaces window.prompt.
  // Enter commits, Escape cancels, focus returns to the select.
  function openLocationInput(locSel, pl, onChange) {
    locSel.value = pl.location || '';
    var host = locSel.closest('.my-garden__field-row') || locSel.closest('label') || locSel.parentNode;
    if (!host || host.querySelector('.my-garden__loc-add')) return;

    var row = document.createElement('span');
    row.className = 'my-garden__loc-add';
    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'my-garden__loc-add-input';
    input.placeholder = 'e.g. front garden pot';
    input.setAttribute('aria-label', 'Name of the new location');
    var save = node('<button type="button" class="button button--outline button--small">Save</button>');
    var cancel = node('<button type="button" class="my-garden__link-btn">Cancel</button>');
    row.appendChild(input);
    row.appendChild(save);
    row.appendChild(cancel);
    host.appendChild(row);
    input.focus();

    function close() {
      if (row.parentNode) row.parentNode.removeChild(row);
      locSel.focus();
    }
    function commit() {
      var name = input.value.trim();
      if (!name) { input.focus(); return; }
      var s = read();
      if (s.locations.indexOf(name) === -1 && page.defaultLocations.indexOf(name) === -1) {
        s.locations.push(name);
      }
      var exists = false;
      for (var i = 0; i < locSel.options.length; i++) {
        if (locSel.options[i].value === name) { exists = true; break; }
      }
      if (!exists) {
        var opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        locSel.insertBefore(opt, locSel.lastElementChild); // before the "+ Add…" option
      }
      locSel.value = name;
      write(s);
      updatePlanting(pl.id, { location: name });
      pl.location = name;
      onChange();
      close();
    }
    save.addEventListener('click', commit);
    cancel.addEventListener('click', close);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
  }

  function savedCard(p) {
    var variantId = p.first_available_variant ? p.first_available_variant.id : (p.variants[0] && p.variants[0].id);
    return node(
      '<li class="my-garden__item my-garden__item--saved">' +
        '<a class="my-garden__item-image" href="' + esc(p.url) + '">' + (p.featured_image ? '<img src="' + esc(p.featured_image) + '&width=200" alt="" loading="lazy">' : '') + '</a>' +
        '<div class="my-garden__item-body">' +
          '<a class="my-garden__item-title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          '<span class="my-garden__item-price">' + money(p.price) + '</span>' +
        '</div>' +
        '<div class="my-garden__item-actions">' +
          (p.available && variantId
            ? '<form action="/cart/add" method="post"><input type="hidden" name="id" value="' + variantId + '"><button type="submit" class="button button--solid button--small">Add to basket</button></form>'
            : '<span class="my-garden__soldout">Unavailable</span>') +
          '<button type="button" class="my-garden__link-btn" data-garden-remove-saved="' + esc(p.handle) + '">Remove</button>' +
        '</div>' +
      '</li>'
    );
  }

  /* ---------- toolbar: add rose, bulk task, print, dismissed ---------- */

  document.addEventListener('click', function (e) {
    var root = document.querySelector('[data-my-garden]');
    if (!root) return;

    if (e.target.closest('[data-garden-print]')) { window.print(); return; }

    if (e.target.closest('[data-garden-remove-saved]')) {
      var btn = e.target.closest('[data-garden-remove-saved]');
      var h = btn.getAttribute('data-garden-remove-saved');
      var s = read();
      var savedIdx = s.saved.indexOf(h);
      if (savedIdx === -1) return;
      s.saved.splice(savedIdx, 1);
      write(s);

      var li = btn.closest('.my-garden__item');
      if (li) li.remove();
      page.savedKey = savedOnlyHandles(read()).slice().sort().join(',');
      if (page.savedGroup && !page.savedList.children.length) page.savedGroup.hidden = true;
      refreshAux(read());

      toast('Rose removed from My Garden', 'Undo', function () {
        var s2 = read();
        if (s2.saved.indexOf(h) === -1) s2.saved.splice(Math.min(savedIdx, s2.saved.length), 0, h);
        write(s2);
        renderSaved(read(), true);
        refreshAux(read());
        toast('Rose restored to My Garden');
      });
      return;
    }

    if (e.target.closest('[data-garden-add-rose]')) {
      var addform = root.querySelector('[data-garden-addform]');
      addform.hidden = false;
      root.querySelector('[data-garden-bulkform]').hidden = true;
      addform.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var nameField = addform.querySelector('[data-garden-add-name]');
      if (nameField) nameField.focus();
      return;
    }

    if (e.target.closest('[data-garden-add-note]')) {
      // "Add a garden note" opens an existing rose's journal, focused on its
      // notes field - a note belongs to a rose's journal, not to the garden
      // in the abstract.
      var s5 = read();
      var first = s5.plantings[0];
      if (!first) {
        toast('Add a rose first, then you can note how it’s doing');
        root.querySelector('[data-garden-add-rose]').click();
        return;
      }
      focusNotesOnOpen = true;
      location.hash = '#/rose/' + encodeURIComponent(first.id);
      return;
    }
    if (e.target.closest('[data-garden-add-cancel]')) { root.querySelector('[data-garden-addform]').hidden = true; return; }
    if (e.target.closest('[data-garden-add-save]')) {
      var name = root.querySelector('[data-garden-add-name]').value.trim();
      if (!name) return;
      var qty = parseInt(root.querySelector('[data-garden-add-qty]').value, 10) || 1;
      var s2 = read();
      var fresh = { id: uid(), handle: '', title: name, custom: true, bareRoot: false,
        image: '', url: '', price: 0, qty: qty, orderedOn: '',
        location: '', locationNote: '', plantedOn: '', notes: '', treatments: [], updatedAt: nowIso() };
      s2.plantings.unshift(fresh);
      write(s2);
      root.querySelector('[data-garden-add-name]').value = '';
      root.querySelector('[data-garden-add-qty]').value = 1;
      root.querySelector('[data-garden-addform]').hidden = true;
      var addedCard = plantingCard(fresh);
      page.list.insertBefore(addedCard, page.list.firstChild);
      refreshAux(read());
      addedCard.querySelector('.my-garden__toggle-journal').focus();
      return;
    }

    if (e.target.closest('[data-garden-bulk]')) {
      var bf = root.querySelector('[data-garden-bulkform]');
      var sel = bf.querySelector('[data-garden-bulk-type]');
      if (!sel.options.length) sel.innerHTML = TREATMENTS.map(function (t) { return '<option>' + t + '</option>'; }).join('');
      bf.querySelector('[data-garden-bulk-date]').value = new Date().toISOString().slice(0, 10);
      bf.hidden = false;
      root.querySelector('[data-garden-addform]').hidden = true;
      return;
    }
    if (e.target.closest('[data-garden-bulk-cancel]')) { root.querySelector('[data-garden-bulkform]').hidden = true; return; }
    if (e.target.closest('[data-garden-bulk-save]')) {
      var bf2 = root.querySelector('[data-garden-bulkform]');
      var entry = {
        type: bf2.querySelector('[data-garden-bulk-type]').value,
        date: bf2.querySelector('[data-garden-bulk-date]').value || new Date().toISOString().slice(0, 10),
        note: bf2.querySelector('[data-garden-bulk-note]').value.trim()
      };
      var s3 = read();
      if (!s3.plantings.length) return;
      s3.plantings.forEach(function (pl) {
        pl.treatments = pl.treatments || [];
        pl.treatments.push(Object.assign({ id: uid() }, entry));
        pl.updatedAt = nowIso();
      });
      write(s3);
      bf2.querySelector('[data-garden-bulk-note]').value = '';
      bf2.hidden = true;
      draw();
      return;
    }

    if (e.target.closest('[data-garden-show-dismissed]')) {
      var s4 = read(); s4.dismissed = []; write(s4);
      mergeOrders(); draw();
      return;
    }
  });

  /* ---------- init ---------- */

  function init() {
    paintButtons();
    paintCount();
    var root = document.querySelector('[data-my-garden]');
    if (root) renderPage(root);
    else if (syncEnabled) pullGarden();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  if ('MutationObserver' in window) {
    new MutationObserver(function () { paintButtons(); }).observe(document.body, { childList: true, subtree: true });
  }
})();
